<template>
  <v-container fluid class="pa-4">
    <!-- File Upload Section -->
    <v-row v-if="currentStep === 'upload'">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title>
            <v-icon left color="primary">mdi-upload</v-icon>
            Upload Excel File
          </v-card-title>
          <v-card-text>
            <v-file-input v-model="selectedFile" accept=".xlsx,.xls" label="Select Excel file" outlined
              prepend-icon="mdi-file-excel" show-size @change="handleFileSelect">
            </v-file-input>

            <v-alert v-if="fileError" type="error" dense class="mt-2">
              {{ fileError }}
            </v-alert>

            <!-- Download Categories Button -->
            <div class="mb-4">
              <v-btn color="success" outlined @click="downloadCategoriesCSV">
                <v-icon left>mdi-download</v-icon>
                Download Available Categories
              </v-btn>
              <div class="caption grey--text mt-1">
                Download a CSV file with all available categories for reference
              </div>
            </div>

            <!-- Expected Excel Format -->
            <v-card outlined class="mt-4">
              <v-card-title class="subtitle-2 py-2">
                Expected Excel Format
              </v-card-title>
              <v-card-text class="pt-2">
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th>Location</th>
                        <th>Barcode</th>
                        <th>Category</th>
                        <th>Brand</th>
                        <th>RRP</th>
                        <th>Gender</th>
                        <th>Size</th>
                        <th>Condition</th>
                        <th>Style Name (Optional)</th>
                        <th>Chest (cm) (Optional)</th>
                        <th>Waist (cm) (Optional)</th>
                        <th>Hips (cm) (Optional)</th>
                        <th>Length (cm) (Optional)</th>
                        <th>Shoulders (cm) (Optional)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>A1-B2</td>
                        <td>1234567890</td>
                        <td>Sneakers</td>
                        <td>Nike</td>
                        <td>99.99</td>
                        <td>Men</td>
                        <td>42</td>
                        <td>Good</td>
                        <td>Air Max 90</td>
                        <td>96</td>
                        <td>80</td>
                        <td>100</td>
                        <td>65</td>
                        <td>42</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
                <div class="mt-2 caption grey--text">
                  Note: Style Name and all measurement columns (Chest, Waist, Hips, Length, Shoulders) are optional.
                  Condition should match your system conditions (e.g., Good, Excellent, With box, etc.).
                  Measurements should be provided in centimetres.
                </div>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Preview Section -->
    <v-row v-if="currentStep === 'preview'">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title>
            <v-icon left color="success">mdi-eye</v-icon>
            Preview Import Data
            <v-spacer />
            <v-chip :color="validItems.length > 0 ? 'success' : 'warning'">
              {{ validItems.length }} valid items
            </v-chip>
            <v-chip v-if="invalidItems.length > 0" color="error" class="ml-2">
              {{ invalidItems.length }} invalid items
            </v-chip>
          </v-card-title>

          <v-card-text class="pa-0">
            <v-tabs v-model="previewTab">
              <v-tab>Valid Items ({{ validItems.length }})</v-tab>
              <v-tab v-if="invalidItems.length > 0">Invalid Items ({{ invalidItems.length }})</v-tab>
            </v-tabs>

            <v-tabs-items v-model="previewTab">
              <v-tab-item>
                <v-data-table :headers="previewHeaders" :items="validItems" :items-per-page="10" class="elevation-0"
                  dense>
                  <template v-slot:item.Brand="{ item }">
                    {{ typeof item.Brand === 'object' ? item.Brand.brandName : item.Brand }}
                  </template>
                  <template v-slot:item.Size="{ item }">
                    <span v-if="typeof item.Size === 'object' && item.Size">
                      <div v-for="(value, key) in item.Size" :key="key">
                        <strong>{{ key }}:</strong> {{ value }}
                      </div>
                    </span>
                    <span v-else>{{ item.Size || 'N/A' }}</span>
                  </template>
                  <template v-slot:item.Condition="{ item }">
                    {{ item.Condition }}
                  </template>
                  <template v-slot:item.RRP="{ item }">
                    {{ item.RRP ? `€${item.RRP}` : '' }}
                  </template>
                  <template v-slot:item.Measurements="{ item }">
                    <span v-if="item.Measurements && Object.keys(item.Measurements).length > 0">
                      <div v-for="(val, key) in item.Measurements" :key="key">
                        <strong>{{ key }}:</strong> {{ val }}cm
                      </div>
                    </span>
                    <span v-else class="grey--text">—</span>
                  </template>
                  <template v-slot:item.validation="{}">
                    <v-icon color="success">mdi-check-circle</v-icon>
                  </template>
                </v-data-table>
              </v-tab-item>

              <v-tab-item v-if="invalidItems.length > 0">
                <v-data-table :headers="[...previewHeaders, { text: 'Errors', value: 'errors' }]" :items="invalidItems"
                  :items-per-page="10" class="elevation-0" dense>
                  <template v-slot:item.validation="{}">
                    <v-icon color="error">mdi-alert-circle</v-icon>
                  </template>
                  <template v-slot:item.errors="{ item }">
                    <v-chip v-for="error in item.errors" :key="error" color="error" small class="ma-1">
                      {{ error }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>

          <v-card-actions>
            <v-btn text @click="goBack">
              Back
            </v-btn>
            <v-spacer />
            <v-btn color="primary" :disabled="validItems.length === 0" @click="startImport">
              Import {{ validItems.length }} Items
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Import Progress Section -->
    <v-row v-if="currentStep === 'importing'">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title>
            <v-icon left color="primary">mdi-progress-clock</v-icon>
            Importing Items...
          </v-card-title>

          <v-card-text>
            <div class="text-center mb-4">
              <v-progress-circular :value="importProgress" :size="80" :width="8" color="primary">
                {{ Math.round(importProgress) }}%
              </v-progress-circular>
            </div>

            <v-progress-linear :value="importProgress" color="primary" height="8" striped class="mb-4" />

            <div class="text-center">
              <div class="subtitle-1 mb-2">
                Processing item {{ currentImportIndex + 1 }} of {{ validItems.length }}
              </div>
              <div class="body-2 grey--text">
                {{ currentItemName || 'Preparing...' }}
              </div>
            </div>

            <v-row class="mt-4" v-if="importResults.successful.length > 0 || importResults.failed.length > 0">
              <v-col cols="4" class="text-center">
                <v-icon color="success" size="24">mdi-check-circle</v-icon>
                <div class="subtitle-2">{{ importResults.successful.length }}</div>
                <div class="caption">Successful</div>
              </v-col>
              <v-col cols="4" class="text-center">
                <v-icon color="error" size="24">mdi-alert-circle</v-icon>
                <div class="subtitle-2">{{ importResults.failed.length }}</div>
                <div class="caption">Failed</div>
              </v-col>
              <v-col cols="4" class="text-center">
                <v-icon color="grey" size="24">mdi-clock-outline</v-icon>
                <div class="subtitle-2">{{ validItems.length - importResults.successful.length - importResults.failed.length }}</div>
                <div class="caption">Remaining</div>
              </v-col>
            </v-row>

            <v-alert v-if="latestError" type="error" dense class="mt-2">
              <strong>Latest Error:</strong> {{ latestError }}
            </v-alert>
          </v-card-text>

          <v-card-actions v-if="importInProgress">
            <v-spacer />
            <v-btn color="error" outlined @click="cancelImport">
              Cancel Import
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Results Section -->
    <v-row v-if="currentStep === 'results'">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title>
            <v-icon left :color="getResultsIconColor()">
              {{ getResultsIcon() }}
            </v-icon>
            {{ getResultsTitle() }}
          </v-card-title>

          <v-card-text>
            <v-alert v-if="importResults.successful.length === 0 && importResults.failed.length > 0"
                     type="error" class="mb-4">
              <strong>Import Failed!</strong> All {{ importResults.failed.length }} items failed to import.
              Please check the errors below and fix your data before trying again.
            </v-alert>
            <v-alert v-else-if="importResults.failed.length > 0"
                     type="warning" class="mb-4">
              <strong>Partial Success!</strong> {{ importResults.successful.length }} items imported successfully,
              but {{ importResults.failed.length }} items failed. Check the errors below.
            </v-alert>
            <v-alert v-else-if="importResults.successful.length > 0"
                     type="success" class="mb-4">
              <strong>Import Complete!</strong> All {{ importResults.successful.length }} items imported successfully.
            </v-alert>

            <v-row class="mb-4">
              <v-col cols="4" class="text-center">
                <v-icon color="success" size="48">mdi-check-circle</v-icon>
                <div class="headline">{{ importResults.successful.length }}</div>
                <div class="subtitle-1">Items Imported Successfully</div>
              </v-col>
              <v-col cols="4" class="text-center">
                <v-icon color="error" size="48">mdi-alert-circle</v-icon>
                <div class="headline">{{ importResults.failed.length }}</div>
                <div class="subtitle-1">Items Failed to Import</div>
              </v-col>
              <v-col cols="4" class="text-center">
                <v-icon color="primary" size="48">mdi-clock-outline</v-icon>
                <div class="headline">{{ Math.round(importDuration / 1000) }}s</div>
                <div class="subtitle-1">Total Time</div>
              </v-col>
            </v-row>

            <v-expansion-panels v-if="importResults.failed.length > 0" class="mt-4" :value="0">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <span class="error--text font-weight-bold">
                    <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
                    View Failed Items ({{ importResults.failed.length }})
                  </span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-data-table :headers="[
                    { text: 'Row', value: 'originalIndex', width: '80px' },
                    { text: 'Barcode', value: 'Barcode' },
                    { text: 'Category', value: 'Category' },
                    { text: 'Brand', value: 'Brand' },
                    { text: 'Error Details', value: 'error', sortable: false }
                  ]" :items="importResults.failed" dense :items-per-page="10">
                    <template v-slot:item.originalIndex="{ item }">
                      <v-chip color="grey" small>Row {{ item.originalIndex }}</v-chip>
                    </template>
                    <template v-slot:item.error="{ item }">
                      <v-tooltip bottom max-width="400">
                        <template v-slot:activator="{ on, attrs }">
                          <v-chip color="error" small v-bind="attrs" v-on="on" class="error-chip">
                            {{ truncateError(item.error) }}
                          </v-chip>
                        </template>
                        <span>{{ item.error }}</span>
                      </v-tooltip>
                    </template>
                  </v-data-table>

                  <div class="mt-3">
                    <v-btn color="error" outlined small @click="exportFailedItems">
                      <v-icon left small>mdi-download</v-icon>
                      Export Failed Items
                    </v-btn>
                    <div class="caption grey--text mt-1">
                      Download failed items as CSV to fix and re-import
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>

          <v-card-actions>
            <v-btn text @click="resetImport">
              Import Another File
            </v-btn>
            <v-spacer />
            <v-btn v-if="importResults.successful.length > 0" color="primary" @click="closeDialog">
              Done
            </v-btn>
            <v-btn v-else color="primary" outlined @click="closeDialog">
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import * as XLSX from 'xlsx'

export default {
  name: 'BulkImportComponent',
  props: {
    location: {
      type: String,
      required: true
    },
    selectedType: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      currentStep: 'upload',
      selectedFile: null,
      fileError: '',
      previewTab: 0,
      importInProgress: false,
      latestError: '',

      rawData: [],
      validItems: [],
      invalidItems: [],

      currentImportIndex: 0,
      currentItemName: '',
      importStartTime: null,
      importDuration: 0,
      importResults: {
        successful: [],
        failed: []
      },

      // Measurement column names as they should appear in the Excel file
      measurementColumns: ['Chest', 'Waist', 'Hips', 'Length', 'Shoulders'],

      previewHeaders: [
        { text: 'Location', value: 'Location', sortable: false },
        { text: 'Barcode', value: 'Barcode', sortable: false },
        { text: 'Category', value: 'Category', sortable: false },
        { text: 'Brand', value: 'Brand', sortable: false },
        { text: 'RRP', value: 'RRP', sortable: false },
        { text: 'Gender', value: 'Gender', sortable: false },
        { text: 'Size', value: 'Size', sortable: false },
        { text: 'Condition', value: 'Condition', sortable: false },
        { text: 'Style Name', value: 'StyleName', sortable: false },
        { text: 'Measurements', value: 'Measurements', sortable: false },
        { text: 'Status', value: 'validation', sortable: false }
      ],

      categories: [],
      brands: [],
      genders: [],
      conditions: []
    }
  },
  computed: {
    importProgress() {
      if (this.validItems.length === 0) return 0
      return (this.currentImportIndex / this.validItems.length) * 100
    }
  },
  async mounted() {
    await this.loadDropdownData()
  },
  methods: {
    async loadDropdownData() {
      try {
        const [categoriesResponse, brandsResponse, dropdownsResponse] = await Promise.all([
          this.$store.dispatch('getCategories'),
          this.$store.dispatch('getBrands'),
          this.$store.dispatch('getDropdowns')
        ])

        this.categories = categoriesResponse.data
        this.brands = brandsResponse.data
        this.genders = dropdownsResponse.data.gender || []

        const rawConditions = dropdownsResponse.data.condition || []
        if (rawConditions.length > 0 && typeof rawConditions[0] === 'string') {
          this.conditions = rawConditions.map(condition => ({ condition: condition }))
        } else {
          this.conditions = rawConditions
        }
      } catch (error) {
        console.error('Error loading dropdown data:', error)
        this.fileError = 'Failed to load required data. Please try again.'
      }
    },

    downloadCategoriesCSV() {
      const link = document.createElement('a')
      link.href = '/available_categories.csv'
      link.download = 'available_categories.csv'
      link.click()
    },

    async handleFileSelect(file) {
      this.fileError = ''

      if (!file) {
        this.rawData = []
        return
      }

      if (!file.name.match(/\.(xlsx|xls)$/)) {
        this.fileError = 'Please select a valid Excel file (.xlsx or .xls)'
        return
      }

      try {
        const arrayBuffer = await file.arrayBuffer()
        const workbook = XLSX.read(arrayBuffer, { type: 'array' })
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]
        const jsonData = XLSX.utils.sheet_to_json(worksheet)

        if (jsonData.length === 0) {
          this.fileError = 'Excel file appears to be empty'
          return
        }

        this.rawData = jsonData
        this.processData()
        this.currentStep = 'preview'
      } catch (error) {
        console.error('Error reading Excel file:', error)
        this.fileError = 'Error reading Excel file. Please check the file format.'
      }
    },

    processData() {
      this.validItems = []
      this.invalidItems = []

      // Check for duplicate barcodes
      const barcodeGroups = {}
      const duplicateWarnings = []

      this.rawData.forEach((row, index) => {
        const barcode = this.sanitizeString(row.Barcode || '')
        if (barcode) {
          if (!barcodeGroups[barcode]) barcodeGroups[barcode] = []
          barcodeGroups[barcode].push({ rowIndex: index + 2, item: row })
        }
      })

      Object.entries(barcodeGroups).forEach(([barcode, items]) => {
        if (items.length > 1) {
          const rowNumbers = items.map(item => item.rowIndex).join(', ')
          duplicateWarnings.push(`Barcode "${barcode}" appears ${items.length} times (rows: ${rowNumbers})`)
        }
      })

      if (duplicateWarnings.length > 0) {
        this.fileError = `Warning: Found duplicate barcodes that will create separate products:\n${duplicateWarnings.join('\n')}`
      }

      this.rawData.forEach((row, index) => {
        const item = {
          originalIndex: index + 2,
          Location: this.sanitizeString(row.Location || ''),
          Barcode: this.sanitizeString(row.Barcode || ''),
          Category: this.sanitizeString(row.Category || ''),
          Brand: this.sanitizeString(row.Brand || ''),
          RRP: this.parseNumber(row.RRP),
          Gender: this.sanitizeString(row.Gender || ''),
          Size: this.sanitizeString(row.Size || ''),
          Condition: this.sanitizeString(row.Condition || ''),
          StyleName: this.sanitizeString(row['Style Name'] || row.StyleName || ''),
          // Parse each measurement column — accept both "Chest" and "Chest (cm)" header variants
          Measurements: this.parseMeasurements(row)
        }

        const errors = this.validateItem(item)

        if (errors.length === 0) {
          const transformedItem = this.transformToProductFormat(item)
          this.validItems.push(transformedItem)
        } else {
          item.errors = errors
          this.invalidItems.push(item)
        }
      })
    },

    /**
     * Reads all measurement columns from a raw Excel row.
     * Accepts both "Chest" and "Chest (cm)" as column headers.
     * Only includes measurements that have a valid positive number.
     */
    parseMeasurements(row) {
      const measurements = {}
      this.measurementColumns.forEach(col => {
        // Try both "Chest" and "Chest (cm)" header variants
        const rawValue = row[col] !== undefined ? row[col] : row[`${col} (cm)`]
        const parsed = this.parseNumber(rawValue)
        if (parsed !== null && parsed > 0) {
          measurements[col] = parsed
        }
      })
      return Object.keys(measurements).length > 0 ? measurements : null
    },

    validateItem(item) {
      const errors = []

      if (!item.Category || item.Category.trim() === '') {
        errors.push('Category required')
      }
      if (!item.Brand || item.Brand.trim() === '') {
        errors.push('Brand required')
      }

      if (item.RRP !== null && item.RRP !== undefined && item.RRP !== '') {
        const rrpValue = parseFloat(item.RRP)
        if (isNaN(rrpValue) || rrpValue <= 0) {
          errors.push('RRP must be a positive number if provided')
        }
      }

      if (item.Brand && item.Brand.trim() !== '') {
        const brandMatch = this.brands.find(b =>
          b.brandName.toLowerCase() === item.Brand.toLowerCase()
        )
        if (!brandMatch) {
          errors.push(`Brand "${item.Brand}" not found in system`)
        }
      }

      if (item.Gender && item.Gender.trim() !== '') {
        if (!this.genders.includes(item.Gender)) {
          errors.push('Invalid gender')
        }
      }

      if (item.Condition && item.Condition.trim() !== '') {
        const conditionMatch = this.conditions.find(c =>
          c.condition.toLowerCase() === item.Condition.toLowerCase()
        )
        if (!conditionMatch) {
          errors.push(`Condition "${item.Condition}" not found in system`)
        }
      }

      // Barcode: only validate length if a barcode is actually provided
      if (item.Barcode && item.Barcode.length < 8) {
        errors.push(`Barcode "${item.Barcode}" must be at least 8 characters`)
      }

      // Measurements: only validate values if measurements are provided
      if (item.Measurements) {
        Object.entries(item.Measurements).forEach(([key, val]) => {
          if (isNaN(val) || val <= 0) {
            errors.push(`Measurement "${key}" must be a positive number`)
          }
        })
      }

      return errors
    },

    transformToProductFormat(item) {
      let brandName = ''
      let brandObj = null

      if (typeof item.Brand === 'string') {
        brandName = item.Brand
        brandObj = this.brands.find(b =>
          b.brandName.toLowerCase() === brandName.toLowerCase()
        )
      } else if (typeof item.Brand === 'object' && item.Brand.brandName) {
        brandObj = item.Brand
        brandName = item.Brand.brandName
      } else {
        brandName = String(item.Brand || '')
        brandObj = this.brands.find(b =>
          b.brandName.toLowerCase() === brandName.toLowerCase()
        )
      }

      let currency = 'EUR'
      if (brandObj?.originalCurrency) {
        if (Array.isArray(brandObj.originalCurrency)) {
          currency = brandObj.originalCurrency[0] || 'EUR'
        } else {
          currency = brandObj.originalCurrency
        }
      }

      let conditionValue = 'Good'
      if (item.Condition) {
        const conditionObj = this.conditions.find(c =>
          c.condition.toLowerCase() === item.Condition.toLowerCase()
        )
        if (conditionObj) {
          conditionValue = conditionObj.condition
        }
      }

      return {
        ...item,
        Type: this.selectedType,
        Location: item.Location || this.location,
        Brand: brandName,
        BrandObject: brandObj,
        Currency: currency,
        Condition: conditionValue,
        Photos: [],
        Size: this.formatSizeData(item.Size, item.Category),
        SizeData: this.formatSizeData(item.Size, item.Category),
        StyleName: item.StyleName || '',
        // Only include Measurements if at least one value exists
        Measurements: item.Measurements && Object.keys(item.Measurements).length > 0
          ? item.Measurements
          : undefined
      }
    },

    getSizeFieldName(category) {
      if (!category) return 'Size'
      const categoryLower = category.toLowerCase()
      if (
        categoryLower.includes('sneaker') ||
        categoryLower.includes('shoe') ||
        categoryLower.includes('boot') ||
        categoryLower.includes('loafer') ||
        categoryLower.includes('sandal') ||
        categoryLower.includes('heel') ||
        categoryLower.includes('trainer')
      ) {
        return 'EUR Shoe Size'
      } else if (
        categoryLower.includes('dress') ||
        categoryLower.includes('t-shirt') ||
        categoryLower.includes('top') ||
        categoryLower.includes('jacket') ||
        categoryLower.includes('pant') ||
        categoryLower.includes('jean') ||
        categoryLower.includes('skirt')
      ) {
        return 'Size'
      } else if (categoryLower.includes('ring')) {
        return 'Ring Size'
      }
      return 'Size'
    },

    formatSizeData(size, category) {
      if (!size) return {}
      if (typeof size === 'object') return size
      const sizeFieldName = this.getSizeFieldName(category)
      return { [sizeFieldName]: size }
    },

    sanitizeString(str) {
      if (typeof str === 'object') return String(str) || ''
      return typeof str === 'string' ? str.trim() : String(str || '').trim()
    },

    parseNumber(value) {
      if (typeof value === 'object') value = String(value)
      const num = parseFloat(value)
      return isNaN(num) ? null : num
    },

    goBack() {
      this.currentStep = 'upload'
      this.rawData = []
      this.validItems = []
      this.invalidItems = []
    },

    async startImport() {
      this.currentStep = 'importing'
      this.currentImportIndex = 0
      this.importStartTime = Date.now()
      this.importResults = { successful: [], failed: [] }
      this.importInProgress = true
      this.latestError = ''

      for (let i = 0; i < this.validItems.length; i++) {
        if (!this.importInProgress) break

        this.currentImportIndex = i
        const item = this.validItems[i]
        this.currentItemName = `${item.Brand || ''} ${item.Category || ''} (${item.Barcode || 'No barcode'})`

        let importSuccessful = false
        let errorMessage = ''

        try {
          const result = await this.importSingleItem(item)
          this.importResults.successful.push({ ...item, result })
          importSuccessful = true
          this.latestError = ''
        } catch (error) {
          errorMessage = error.message || 'Unknown error'
          this.latestError = `Row ${item.originalIndex}: ${errorMessage}`
          this.importResults.failed.push({ ...item, error: errorMessage })
        }

        console.log(`Item ${i + 1} status:`, {
          successful: importSuccessful,
          error: errorMessage,
          totalSuccessful: this.importResults.successful.length,
          totalFailed: this.importResults.failed.length
        })

        await new Promise(resolve => setTimeout(resolve, 100))
      }

      this.importDuration = Date.now() - this.importStartTime
      this.importInProgress = false
      this.currentStep = 'results'

      this.$emit('importCompleted', {
        successful: this.importResults.successful.length,
        failed: this.importResults.failed.length,
        duration: this.importDuration,
        totalAttempted: this.validItems.length,
        wasSuccessful: this.importResults.failed.length === 0,
        wasPartialSuccess: this.importResults.successful.length > 0 && this.importResults.failed.length > 0
      })
    },

    cancelImport() {
      this.importInProgress = false
      this.latestError = 'Import cancelled by user'
    },

    async importSingleItem(item) {
      try {
        const brandObj = this.brands.find(b =>
          b.brandName.toLowerCase().trim() === item.Brand.toLowerCase().trim()
        )
        if (!brandObj) {
          throw new Error(`Brand "${item.Brand}" not found in system`)
        }

        const validGenders = ['Men', 'Women', 'Unisex', 'Kids']
        if (item.Gender && !validGenders.includes(item.Gender)) {
          throw new Error(`Invalid gender "${item.Gender}". Must be one of: ${validGenders.join(', ')}`)
        }

        const rrpValue = parseFloat(item.RRP)
        if (isNaN(rrpValue) || rrpValue <= 0) {
          throw new Error(`Invalid RRP "${item.RRP}". Must be a positive number`)
        }

        // FIX: only reject barcode if one is provided but too short
        if (item.Barcode && item.Barcode.length < 8) {
          throw new Error(`Barcode "${item.Barcode}" must be at least 8 characters`)
        }

        const productBody = {
          product: {
            Type: this.selectedType,
            Location: item.Location,
            Barcode: item.Barcode ? item.Barcode.toString() : undefined,
            Category: item.Category,
            Brand: item.Brand,
            RRP: rrpValue,
            Currency: item.Currency,
            Condition: item.Condition,
            Gender: item.Gender,
            Photos: [],
            Size: this.formatSizeData(item.Size, item.Category),
            StyleName: item.StyleName || '',
            Notes: `Bulk imported on ${new Date().toLocaleDateString()}`,
            // Only include Measurements in the payload if values exist
            ...(item.Measurements && Object.keys(item.Measurements).length > 0
              ? { Measurements: item.Measurements }
              : {})
          }
        }

        console.log('Attempting to import product:', productBody)

        const response = await this.$store.dispatch('postProduct', productBody)
        console.log('Import successful for barcode:', item.Barcode, response)
        return response

      } catch (error) {
        console.error('Import failed for item:', item, error)

        if (error.response) {
          const status = error.response.status
          const message = error.response.data

          switch (status) {
            case 400: {
              if (typeof message === 'string') {
                if (message.includes('duplicate') || message.includes('already exists')) {
                  throw new Error(`Duplicate barcode "${item.Barcode}" - product already exists`)
                } else if (message.includes('validation') || message.includes('invalid')) {
                  throw new Error(`Validation error: ${message}`)
                } else if (message.includes('category')) {
                  throw new Error(`Invalid category "${item.Category}" - category not found in system`)
                } else {
                  throw new Error(`Invalid data: ${message}`)
                }
              } else if (typeof message === 'object') {
                throw new Error(`Validation error: ${message.error || message.message || JSON.stringify(message)}`)
              } else {
                throw new Error('Invalid product data provided')
              }
            }
            case 401:
              throw new Error('Authentication failed - please log in again')
            case 403:
              throw new Error('Access denied - insufficient permissions')
            case 404:
              throw new Error('API endpoint not found - please contact support')
            case 409:
              throw new Error(`Conflict: Barcode "${item.Barcode}" already exists in system`)
            case 422: {
              if (typeof message === 'object') {
                throw new Error(`Data validation failed: ${message.error || message.message || JSON.stringify(message)}`)
              }
              throw new Error(`Data validation failed: ${message || 'Invalid product data'}`)
            }
            case 500: {
              let serverError = 'Server error - please try again later'
              if (typeof message === 'string') {
                serverError = `Server error: ${message}`
              } else if (typeof message === 'object' && message) {
                serverError = `Server error: ${message.error || message.message || JSON.stringify(message)}`
              }
              throw new Error(serverError)
            }
            default: {
              let defaultError = `Server error (${status})`
              if (typeof message === 'string') {
                defaultError += `: ${message}`
              } else if (typeof message === 'object' && message) {
                defaultError += `: ${message.error || message.message || JSON.stringify(message)}`
              }
              throw new Error(defaultError)
            }
          }
        } else if (error.message) {
          throw error
        } else {
          throw new Error('Network error - please check your connection and try again')
        }
      }
    },

    getResultsIcon() {
      if (this.importResults.successful.length === 0 && this.importResults.failed.length > 0) return 'mdi-alert-circle'
      if (this.importResults.failed.length > 0) return 'mdi-check-circle-outline'
      return 'mdi-check-circle'
    },

    getResultsIconColor() {
      if (this.importResults.successful.length === 0 && this.importResults.failed.length > 0) return 'error'
      if (this.importResults.failed.length > 0) return 'warning'
      return 'success'
    },

    getResultsTitle() {
      if (this.importResults.successful.length === 0 && this.importResults.failed.length > 0) return 'Import Failed'
      if (this.importResults.failed.length > 0) return 'Import Partially Complete'
      return 'Import Complete'
    },

    truncateError(error) {
      if (!error || typeof error !== 'string') return 'Unknown error'
      return error.length > 50 ? error.substring(0, 50) + '...' : error
    },

    exportFailedItems() {
      if (this.importResults.failed.length === 0) return

      const headers = [
        'Row', 'Location', 'Barcode', 'Category', 'Brand', 'RRP',
        'Gender', 'Size', 'Condition', 'Style Name',
        'Chest', 'Waist', 'Hips', 'Length', 'Shoulders',
        'Error'
      ]

      const csvContent = [
        headers.join(','),
        ...this.importResults.failed.map(item => {
          const m = item.Measurements || {}
          return [
            item.originalIndex,
            `"${item.Location || ''}"`,
            `"${item.Barcode || ''}"`,
            `"${item.Category || ''}"`,
            `"${item.Brand || ''}"`,
            item.RRP || '',
            `"${item.Gender || ''}"`,
            `"${item.Size || ''}"`,
            `"${item.Condition || ''}"`,
            `"${item.StyleName || ''}"`,
            m.Chest || '',
            m.Waist || '',
            m.Hips || '',
            m.Length || '',
            m.Shoulders || '',
            `"${item.error || ''}"`
          ].join(',')
        })
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `failed_imports_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    resetImport() {
      this.currentStep = 'upload'
      this.selectedFile = null
      this.rawData = []
      this.validItems = []
      this.invalidItems = []
      this.importResults = { successful: [], failed: [] }
      this.currentImportIndex = 0
      this.fileError = ''
      this.latestError = ''
      this.importInProgress = false
    },

    closeDialog() {
      if (this.importResults.successful.length > 0) {
        this.$emit('closeDialog')
      } else if (this.importResults.failed.length > 0) {
        const userConfirmed = confirm('All items failed to import. Are you sure you want to close without fixing the errors?')
        if (userConfirmed) this.$emit('closeDialog')
      } else {
        this.$emit('closeDialog')
      }
    }
  }
}
</script>

<style scoped>
.v-progress-circular {
  margin: 1rem;
}

.error-chip {
  max-width: 200px;
  cursor: help;
}

.error-chip .v-chip__content {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>